// Транспорт и хуки раздела «Личный кабинет». ТОЛЬКО ЧТЕНИЕ.
//
// ЧЕМ ЭТОТ РАЗДЕЛ ОТЛИЧАЕТСЯ ОТ ВСЕХ ОСТАЛЬНЫХ, И ПОЧЕМУ ЭТО ВАЖНО ЗДЕСЬ, А НЕ ТОЛЬКО
// НА СЕРВЕРЕ. Во всех прочих разделах фронт сам говорит, про кого спрашивает: «покажи
// инициативы человека 9687», «покажи овертаймы за июль». Здесь он этого не говорит и не
// может: КТО спрашивает, решает BFF по подписанной сессии, а параметр из адресной строки
// он обязан выбросить. Поэтому ни одна функция в этом файле не принимает engineer_bx,
// и добавлять его сюда нельзя: параметр, которого нет в коде фронта, невозможно подставить
// из адресной строки через интерфейс — останется только curl, а его встретит дверь BFF.
//
// РАЗДЕЛ ЖИВЁТ ЗА ФЛАГОМ. Данные кабинета лежат в трёх разделах, закрытых на одну учётку
// владельца (ведомость, инициативы, овертаймы) — там заработок людей поимённо. Показать
// человеку ЕГО СОБСТВЕННЫЕ цифры — отдельное решение владельца, и пока оно не принято,
// раздел выключен флагом. Приём тот же, что у единого входа (`CIR_ID_ENABLED`): флаг
// приезжает в `/api/me`, и выключенный раздел не делает ни одного запроса за данными.
//
// Заявку на выплату отсюда подать нельзя и не будет можно: согласование живёт на портале
// 73, и переносить его в CRM половиной значило бы получить две очереди заявок.

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/profile'

/**
 * Тот же разбор ответа, что в api/client.ts, api/p73.ts, api/p73g.ts, api/p73i.ts.
 *
 * 400 от ядра здесь означает одно: BFF не подставил человека. Это ОТКАЗ, а не «данных
 * нет», и превращать его в пустой кабинет нельзя — пустой кабинет человек прочтёт как
 * «я ничего не сделал», а это про его заработок.
 */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    if (res.status === 403) {
      // У кабинета 403 значит не «вам сюда нельзя по роли», а «раздел ещё не открыт»
      // либо «у вашей учётки нет привязки к сотруднику Bitrix». Второе — самая частая
      // причина, и сказать её словами дешевле, чем разбираться по логам.
      throw new Error('личный кабинет вам пока не открыт: либо раздел ещё выключен, '
                      + 'либо у вашей учётки нет привязки к сотруднику Bitrix — '
                      + 'без неё сервер не знает, чьи данные показывать')
    }
    if (res.status === 404) {
      throw new Error('раздел «Личный кабинет» на сервере не подключён')
    }
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. profile_api.py) ───────────

/** Общая обёртка списка ядра. */
export interface Список<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  /** Кому отвечали. Приезжает эхом — сверять его с чем-либо на экране нечем и незачем. */
  engineer_bx?: number
}

/** Свежесть источника из sync_state. */
export interface СвежестьКабинета {
  entity: string
  last_sync_at: string | null
  last_full: string | null
  last_count: number | null
  age_min: number | null
}

/** Кто я на портале 73. Названия уровня в зеркале НЕТ — см. `notes` в ответе. */
export interface КтоЯ {
  d1_id: number | null
  full_name: string | null
  bitrix_user_id: number | null
  level: number | null
  role: string | null
  is_engineer: boolean | null
  is_active: boolean | null
}

/** Баллы: заработано, выплачено, осталось. Курс портала — 1 балл = 1 ₽. */
export interface ДеньгиКабинета {
  total_points: number | null
  paid_points: number | null
  available_points: number | null
  accruals: number | null
  accrued_sum: number | null
  last_accrual_at: string | null
  payouts: number | null
  paid_rub: number | null
  pending_requests: number | null
  /** Баланс минус журнал. Не ноль — значит зеркало не сходится само с собой. */
  total_minus_log: number | null
}

export interface СерииКабинета {
  current_streak: number | null
  longest_streak: number | null
  login_streak: number | null
  last_login_at: string | null
  course73_done_at: string | null
}

/** Счётчики работы: верификации, отчёты, акты, задания процессов. */
export interface РаботаКабинета {
  verif_total: number
  verif_approved: number
  verif_short_ok: number
  verif_full_ok: number
  verif_initiative: number
  verif_open: number
  verif_last_at: string | null
  reports_total: number
  reports_approved: number
  reports_open: number
  acts_total: number
  acts_last_date: string | null
  bp_live: number
  bp_overdue: number
  bp_oldest_at: string | null
}

/** Инициативы. Все поля могут быть null: «инициатив нет вовсе» — это не «0 ₽». */
export interface ИнициативыКабинета {
  initiatives: number | null
  as_engineer: number | null
  as_repairman: number | null
  counted_deals: number | null
  total_rub: number | null
  counted_rub: number | null
  pending_rub: number | null
  first_date: string | null
  last_date: string | null
}

export interface ОвертаймыКабинета {
  n: number | null
  work_hours: number | null
  road_hours: number | null
  sum_rub: number | null
  approved_n: number | null
  paid_n: number | null
  flagged_n: number | null
}

/**
 * Сводка кабинета. `found: false` — человек не инженер портала 73, и это НЕ ошибка:
 * коммерсант, открывший кабинет, должен прочитать объяснение, а не увидеть нули.
 */
export interface СводкаКабинета {
  engineer_bx: number
  found: boolean
  why?: string
  person?: КтоЯ
  money?: ДеньгиКабинета
  streaks?: СерииКабинета
  badges?: { badges: number | null }
  work?: РаботаКабинета
  initiatives?: ИнициативыКабинета
  overtime?: ОвертаймыКабинета
  overtime_n?: number | null
  trips_n?: number | null
  freshness: СвежестьКабинета[]
  /** Оговорки о неполноте — с сервера, а не текстом на экране. */
  notes: string[]
}

export interface БейджСтрока {
  d1_id: number
  earned_at: string | null
  verification_d1_id: number | null
  code: string | null
  name: string | null
  icon: string | null
  description: string | null
  /** Цена по каталогу НА СЕГОДНЯ, а не то, что начислили тогда. См. bonus_points_note. */
  bonus_points: number | null
}

export interface БейджиИтоги {
  badges: number
  bonus_points: number
  first_at: string | null
  last_at: string | null
  /** Бейдж выдан, а в каталоге его нет. Не должно быть, но если есть — видно. */
  unknown_badge: number
}

export interface НачислениеСтрока {
  d1_id: number
  points: number | null
  reason: string | null
  created_at: string | null
  verification_d1_id: number | null
  /** Догадка ядра по началу строки причины: badge | verif_short | verif_full | … */
  kind: string | null
  period: string | null
}

export interface НачисленияИтоги {
  accruals: number
  points: number
  first_at: string | null
  last_at: string | null
  zero_points: number
  negative: number
  /** Формулировку портала разобрать не удалось. Не ноль — разбивка по видам неполна. */
  unparsed_kind: number
}

export interface ВыплатаСтрока {
  d1_id: number
  amount_rub: number | null
  points_paid: number | null
  period: string | null
  notes: string | null
  created_at: string | null
  created_by_name: string | null
  /** Рубли минус баллы. Не ноль — выплата не сошлась со списанием. */
  rub_minus_points: number | null
}

export interface ВыплатыИтоги {
  payouts: number
  amount_rub: number
  points_paid: number
  periods: number
  first_at: string | null
  last_at: string | null
}

/** Включён ли раздел. Приезжает в `/api/me`, как `sso` у единого входа. */
export interface ФлагКабинета {
  enabled: boolean
  /** Есть ли у учётки привязка к сотруднику Bitrix: без неё кабинет показать нечем. */
  linked: boolean
}

/**
 * Отдельным запросом, а не через fetchMe: форма Me — чужой контракт, её не трогаем.
 * Тот же приём, что у `fetchSso` в api/client.ts, и по той же причине.
 *
 * Ошибку глотаем: не ответил BFF — считаем раздел выключенным. Показать выключенный
 * раздел вместо ошибки безопаснее наоборот: «раздел готов и ждёт решения» человек
 * поймёт, а «HTTP 500» пошлёт его чинить то, что не сломано.
 */
export const запросФлага = async (): Promise<ФлагКабинета> => {
  try {
    const res = await fetch('/api/me', { credentials: 'same-origin' })
    if (!res.ok) return { enabled: false, linked: false }
    const данные = (await res.json()) as {
      profile?: { enabled?: boolean }
      user?: { bitrix_user_id?: number | null }
    }
    return {
      enabled: !!данные.profile?.enabled,
      linked: Number(данные.user?.bitrix_user_id ?? 0) > 0,
    }
  } catch {
    return { enabled: false, linked: false }
  }
}

// ─────────── хуки ───────────

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

/**
 * Включён ли раздел. Отдельный хук, а не поле внутри данных: пока ответа нет, экран
 * обязан молчать, а не решать за сервер. `staleTime` больше обычного — флаг меняется
 * правкой .env и перезапуском, а не по ходу работы.
 */
export function useФлагКабинета() {
  return useQuery<ФлагКабинета>({
    queryKey: ['profile', 'flag'],
    queryFn: запросФлага,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

/**
 * Страницы у постраничников СВОИ, а не общий `ф`: у истории начислений своя страница,
 * у выплат своя, и общий объект фильтров сбрасывал бы одну при листании другой.
 */
export interface Страница {
  [ключ: string]: unknown
  start?: number
  limit?: number
}

function строкаЗапроса(ф: Record<string, unknown>): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? '?' + s : ''
}

/**
 * `enabled` во всех хуках ниже — не оптимизация, а требование: при выключенном флаге
 * не должно уйти НИ ОДНОГО запроса за данными. Раздел не «показывает пустоту», он
 * не спрашивает вовсе, и это проверяется тестом (tests/profile.test.tsx).
 */
export function useСводкаКабинета(включён: boolean) {
  return useQuery<СводкаКабинета>({
    queryKey: ['profile', 'me'],
    queryFn: () => запрос<СводкаКабинета>('/me'),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useМоиБейджи(включён: boolean, ф: Страница) {
  return useQuery<Список<БейджСтрока> & { totals: БейджиИтоги; bonus_points_note: string }>({
    queryKey: ['profile', 'badges', ф],
    queryFn: () => запрос('/badges' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useМоиНачисления(включён: boolean, ф: Страница) {
  return useQuery<Список<НачислениеСтрока> & { totals: НачисленияИтоги }>({
    queryKey: ['profile', 'accruals', ф],
    queryFn: () => запрос('/accruals' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}

export function useМоиВыплаты(включён: boolean, ф: Страница) {
  return useQuery<Список<ВыплатаСтрока> & { totals: ВыплатыИтоги }>({
    queryKey: ['profile', 'payouts', ф],
    queryFn: () => запрос('/payouts' + строкаЗапроса(ф)),
    enabled: включён,
    ...ОБЩЕЕ,
  })
}
